// client/src/pages/LgmProductNavigator.tsx
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import ProductsReview from './ProductsReview';
import ProductMasterReview from './ProductMasterReview';
import ManufacturerReview from './ManufacturerReview';
import Grades from './Grades';
import TherapeuticAreas from './TherapeuticAreas';
import useAuth from 'auth/auth';
import DevTools from 'components/DevTools';
import { DebugLayout } from 'components/DebugLayout';
// import QuotesReview from './QuotesReview';
import { useDeleteAllProducts } from 'services/api/domains/products/hooks';
import AuditLogs from './AuditLogs';
import { useViewportSize } from 'shared/hooks/useViewportSize';

// Lazy load heavy components to reduce initial bundle size
const DashboardMetrics = lazy(() => import('./DashboardMetrics'));
const ManufacturerMap = lazy(() => import('./ManufacturerMap'));

// Loading fallback component for lazy-loaded routes
const LoadingFallback: React.FC<{ message: string }> = ({ message }) => (
  <div style={loadingStyles.containerStyle}>
    <div style={loadingStyles.spinnerStyle} />
    <p style={loadingStyles.textStyle}>{message}</p>
  </div>
);

// ─── Last-view persistence ────────────────────────────────────────────────────
type View =
  | 'products'
  | 'master'
  | 'manufacturer_master'
  | 'manufacturer_map'
  | 'grades'
  | 'therapeutic_areas'
  | 'quotes'
  | 'audit_logs'
  | 'dashboard_metrics';

const LAST_VIEW_KEY = 'lgm-last-view';

const VALID_VIEWS = new Set<string>([
  'products',
  'master',
  'manufacturer_master',
  'manufacturer_map',
  'grades',
  'therapeutic_areas',
  'quotes',
  'audit_logs',
  'dashboard_metrics',
]);

// Views that require admin role
const ADMIN_ONLY_VIEWS = new Set<string>([
  'grades',
  'therapeutic_areas',
  'audit_logs',
]);

function getSavedView(): View {
  try {
    const raw = localStorage.getItem(LAST_VIEW_KEY);
    if (raw !== null && VALID_VIEWS.has(raw)) return raw as View;
  } catch {
    // localStorage unavailable; fall through
  }
  return 'products';
}
// ─────────────────────────────────────────────────────────────────────────────

const LgmProductNavigator: React.FC = () => {
  const [view, setView] = useState<View>(getSavedView);

  const { instance, account, isAuthenticated, isAdmin, roles } = useAuth();

  const [showDevTools, setShowDevTools] = useState(false);
  const deleteAllProductsMutation = useDeleteAllProducts();

  // Restore last-viewed page after authentication, with role gating.
  // Falls back to 'products' if the saved view requires admin and user is not admin.
  useEffect(() => {
    if (account) {
      const saved = getSavedView();
      setView(ADMIN_ONLY_VIEWS.has(saved) && !isAdmin ? 'products' : saved);
    }
  }, [instance, account, isAdmin]);

  // Persist the active view to localStorage so it survives page refreshes
  // and Azure AD authentication redirects.
  useEffect(() => {
    try {
      localStorage.setItem(LAST_VIEW_KEY, view);
    } catch {
      // localStorage unavailable; silently skip
    }
  }, [view]);

  useEffect(() => {
    console.log('🔎 Current view:', view, 'Roles:', roles);
  }, [view, roles]);

  const handleDeleteAll = useCallback(async () => {
    if (!window.confirm('⚠️ Delete ALL products? This cannot be undone.')) {
      return;
    }
    try {
      await deleteAllProductsMutation.mutate();
    } catch (err: unknown) {
      alert(`❌ Failed to delete all products: ${JSON.stringify(err)}`);
    }
  }, [deleteAllProductsMutation]);

  useEffect(() => {
    if (deleteAllProductsMutation.status === 'success') {
      alert('✅ All products deleted successfully');
    }
  }, [deleteAllProductsMutation.status]);

  const { heightClass } = useViewportSize();
  const isSmall = heightClass === 'sm';
  const headerPadding: React.CSSProperties = isSmall
    ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' }
    : {};
  const logoSize: React.CSSProperties = isSmall ? { height: '52px' } : {};
  const mainPadding: React.CSSProperties = isSmall
    ? { paddingTop: '0.75rem', paddingBottom: '0.75rem' }
    : {};
  const activeNavStyle: React.CSSProperties = isSmall
    ? { ...styles.navButtonActiveStyle, fontSize: '0.875rem' }
    : styles.navButtonActiveStyle;

  return (
    <div style={styles.mainContainerStyle}>
      {/* Top Navigation Bar */}
      <div
        style={{ ...styles.headerContainerStyle, ...headerPadding }}
        data-layout-role="app-header">
        <div style={styles.headerContentStyle}>
          <img
            src="/LGMNavigator.png"
            alt="LGM Pharma"
            style={{ ...styles.logoStyle, ...logoSize }}
          />

          {/* Navigation Buttons */}
          <div style={styles.navContainerStyle}>
            <button
              onClick={() => setView('products')}
              style={
                view === 'products' ? activeNavStyle : styles.navButtonStyle
              }>
              Products
            </button>
            <button
              onClick={() => setView('manufacturer_master')}
              style={
                view === 'manufacturer_master'
                  ? activeNavStyle
                  : styles.navButtonStyle
              }>
              Manufacturers
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setView('grades')}
                  style={
                    view === 'grades' ? activeNavStyle : styles.navButtonStyle
                  }>
                  Product Types
                </button>
                <button
                  onClick={() => setView('therapeutic_areas')}
                  style={
                    view === 'therapeutic_areas'
                      ? activeNavStyle
                      : styles.navButtonStyle
                  }>
                  Therapeutic Areas
                </button>
              </>
            )}
            <button
              onClick={() => setView('manufacturer_map')}
              style={
                view === 'manufacturer_map'
                  ? activeNavStyle
                  : styles.navButtonStyle
              }>
              Facilities Map
            </button>
            {/* <button
              disabled
              style={styles.navButtonDisabledStyle}
              title="Disabled for now">
              RFQs
            </button> */}
            <button
              onClick={() => setView('dashboard_metrics')}
              style={
                view === 'dashboard_metrics'
                  ? activeNavStyle
                  : styles.navButtonStyle
              }>
              Metrics
            </button>
            {isAdmin && (
              <button
                onClick={() => setView('audit_logs')}
                style={
                  view === 'audit_logs' ? activeNavStyle : styles.navButtonStyle
                }>
                Audit Logs
              </button>
            )}
          </div>

          <div style={styles.buttonGroupStyle}>
            <button
              onClick={() => setShowDevTools(prev => !prev)}
              style={styles.headerButtonStyle}>
              {showDevTools ? 'Hide Dev Tools' : 'Show Dev Tools'}
            </button>
            <button
              onClick={() => instance.logoutRedirect()}
              style={styles.headerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Developer Tools Panel */}
      {showDevTools && (
        <DevTools
          deleteAllStatus={deleteAllProductsMutation}
          onDeleteAll={handleDeleteAll}
        />
      )}

      {/* Main Content */}
      <div style={{ ...styles.mainContentStyle, ...mainPadding }}>
        {!isAuthenticated ? (
          <p style={styles.waitingTextStyle}>🔐 Waiting for login...</p>
        ) : (
          <>
            {/* Products */}
            {view === 'products' && (
              <ProductsReview deleteAllStatus={deleteAllProductsMutation} />
            )}
            {/* Manufacturers */}
            {view === 'manufacturer_master' && <ManufacturerReview />}
            {/* NOTE: Master Products is not used */}
            {view === 'master' && <ProductMasterReview />}
            {/* Grades */}
            {view === 'grades' && isAdmin && <Grades />}
            {/* Therapeutic Areas */}
            {view === 'therapeutic_areas' && isAdmin && <TherapeuticAreas />}
            {/* Facilities Map */}
            {view === 'manufacturer_map' && (
              <Suspense
                fallback={<LoadingFallback message="Loading facilities..." />}>
                <ManufacturerMap />
              </Suspense>
            )}
            {/* RFQs */}
            {/* {view === 'quotes' && <QuotesReview />} */}
            {/* Dashboard Metrics */}
            {view === 'dashboard_metrics' && (
              <Suspense
                fallback={<LoadingFallback message="Loading metrics..." />}>
                <DashboardMetrics />
              </Suspense>
            )}
            {/* Audit Logs */}
            {view === 'audit_logs' && <AuditLogs />}
          </>
        )}
      </div>
      <DebugLayout />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  mainContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: 'inherit', // font-lgm
  } as React.CSSProperties,
  headerContainerStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb', // border-b
    paddingBottom: '1rem', // pb-4
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1rem', // py-4
  } as React.CSSProperties,
  headerContentStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
  } as React.CSSProperties,
  logoStyle: {
    height: '75px',
    objectFit: 'contain',
  } as React.CSSProperties,
  buttonGroupStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem', // gap-3
  } as React.CSSProperties,
  headerButtonStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
    color: '#1f2937', // text-gray-800
    padding: '0.25rem 0.75rem', // px-3 py-1
    borderRadius: '0.25rem', // rounded
    fontSize: '0.875rem', // text-sm
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  navContainerStyle: {
    display: 'flex',
    gap: '1.5rem', // space-x-6
    color: '#6b7280', // text-gray-500
    fontSize: '0.875rem', // text-sm
    flex: 1,
    justifyContent: 'flex-start',
  } as React.CSSProperties,
  navButtonStyle: {
    color: '#6b7280', // text-gray-500
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  navButtonActiveStyle: {
    color: '#ea580c', // text-orange-600
    fontWeight: 'bold',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25rem',
  } as React.CSSProperties,
  navButtonDisabledStyle: {
    color: '#9ca3af', // text-gray-400
    cursor: 'not-allowed',
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  mainContentStyle: {
    flex: 1,
    overflow: 'auto',
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1.5rem', // py-6
    paddingBottom: '1.5rem',
  } as React.CSSProperties,
  waitingTextStyle: {
    color: '#6b7280', // text-gray-500
  } as React.CSSProperties,
};

const loadingStyles: Record<string, React.CSSProperties> = {
  containerStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    fontFamily: 'var(--font-lgm, sans-serif)',
  },
  spinnerStyle: {
    width: '48px',
    height: '48px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid var(--color-lgmOrange, #f97316)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  textStyle: {
    marginTop: '16px',
    color: '#6b7280',
    fontSize: '14px',
  },
};

export default LgmProductNavigator;
